import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { adminDeleteMission, adminGetMissions, adminToggleHidden } from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import { ErrorBlock, LoadingBlock, PageHeader, Panel } from '@/components/ui'
import { formatNum } from '@/lib/utils'
import { Eye, EyeOff, Pencil, Trash2 } from 'lucide-react'

export default function AdminMissions() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [hidden, setHidden] = useState('')
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-missions', page, q, hidden],
    queryFn: () => adminGetMissions(page, 20, q, hidden),
  })

  const toggle = useMutation({
    mutationFn: (id: number) => adminToggleHidden(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-missions'] }),
  })

  const del = useMutation({
    mutationFn: (id: number) => adminDeleteMission(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-missions'] }),
  })

  return (
    <PageMotion className="space-y-6">
      <PageHeader
        title="Missions"
        subtitle="Edit, hide, or delete imports"
        action={
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <form
              className="flex gap-2 flex-1"
              onSubmit={(e) => {
                e.preventDefault()
                setPage(1)
                setQ(search.trim())
              }}
            >
              <input
                className="input"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="submit" className="btn-primary">
                Search
              </button>
            </form>
            <select
              className="input sm:w-40"
              value={hidden}
              onChange={(e) => {
                setHidden(e.target.value)
                setPage(1)
              }}
            >
              <option value="">All</option>
              <option value="false">Visible</option>
              <option value="true">Hidden</option>
            </select>
          </div>
        }
      />

      <Panel flush>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <div className="p-4">
            <ErrorBlock message="Failed to load missions" />
          </div>
        ) : (
          <>
            <div className="table-wrap border-0 rounded-none">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Map</th>
                    <th>Kills</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.data ?? []).map(
                    (m: {
                      id: number
                      missionName: string
                      worldname: string
                      killCount: number
                      hidden: boolean
                      ocapLink: string
                    }) => (
                      <tr key={m.id}>
                        <td className="font-mono text-xs text-ink-faint">{m.id}</td>
                        <td className="font-medium text-ink">
                          {m.missionName || `Mission #${m.id}`}
                        </td>
                        <td>{m.worldname}</td>
                        <td className="font-mono text-accent-text">{formatNum(m.killCount)}</td>
                        <td>
                          {m.hidden ? (
                            <span className="chip text-amber-400 border-amber-500/30 bg-amber-500/10">
                              Hidden
                            </span>
                          ) : (
                            <span className="chip-accent">Visible</span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-1 justify-end">
                            <button
                              type="button"
                              className="btn-ghost !p-2"
                              title="Toggle visibility"
                              onClick={() => toggle.mutate(m.id)}
                            >
                              {m.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                            <Link to={`/admin/missions/${m.id}`} className="btn-ghost !p-2">
                              <Pencil size={14} />
                            </Link>
                            <button
                              type="button"
                              className="btn-ghost !p-2 text-rose-400"
                              title="Delete"
                              onClick={() => {
                                if (confirm(`Delete mission #${m.id}?`)) del.mutate(m.id)
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
            {data && data.totalPages > 1 && (
              <div className="flex justify-between px-4 py-3 border-t border-line text-sm">
                <span className="text-ink-faint">
                  Page {data.page} / {data.totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </PageMotion>
  )
}
